// Файл interface_customization_store.js

/**
 * Модуль хранит персональный профиль интерфейса текущего пользователя.
 * Он нормализует значения backend и применяет CSS-переменные и
 * data-атрибуты. Компоненты читают отсюда поведенческие настройки без
 * дублирования API-запросов.
 */

import './interface_customization.css';
import { apply_css_values } from './interface_customization_css';



export const DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS = {
    interface_font_size_px: 17,
    interface_density: 'normal',
    main_sidebar_width_px: 100,
    contextual_sidebar_width_px: 100,
    left_sidebar_fit_content: true,
    top_tab_button_height_px: 15,
    left_tab_button_height_px: 30,
    lk_tab_button_height_px: 40,
    tab_gap_px: 5,
    left_tab_gap_px: 5,
    lk_tab_gap_px: 20,
    top_tab_title_padding_px: 8,
    left_tab_title_padding_px: 8,
    input_padding_block_px: 4,
    input_padding_inline_px: 8,
    long_tab_names: 'wrap',
    remember_last_tab: true,
    restore_open_tabs: true,
    reopen_object_mode: 'existing',
    table_row_height_px: 30,
    table_font_size_px: 17,
    sticky_table_header: true,
    scrollbars: 'normal',
    animations: 'normal',
    toast_duration_ms: 10000,
    toast_position: 'top-left',
    unsaved_close_confirmation: 'always',
    start_section: 'last',
    portrait_context_auto_collapse: true,
    workspace_zoom_percent: 100,
    document_register_view: 'none',
    document_showcase_view: 'tiles',
    document_showcase_sort: 'name_asc',
};

const allowed = {
    interface_density: ['compact', 'normal', 'comfortable'],
    long_tab_names: ['wrap', 'ellipsis'],
    reopen_object_mode: ['existing', 'new'],
    scrollbars: ['normal', 'compact', 'hover'],
    animations: ['normal', 'reduced', 'off'],
    toast_position: [
        'top-left', 'top-right', 'bottom-left', 'bottom-right',
    ],
    unsaved_close_confirmation: ['always', 'documents', 'never'],
    start_section: ['last', 'bux', 'lk', 'developer', 'office'],
    document_register_view: ['none', 'source', 'type', 'sender'],
    document_showcase_view: ['list', 'tiles'],
    document_showcase_sort: [
        'name_asc', 'name_desc', 'newest', 'oldest', 'type',
    ],
};
let current_settings = { ...DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS };
let viewport_listener_installed = false;
let settings_load_promise = null;
const SETTINGS_CACHE_KEY = 'potale_interface_customization_settings';

const bounded_number = (value, fallback, minimum, maximum) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {

        return fallback;
    }

    return Math.max(minimum, Math.min(maximum, Math.round(number)));
};

const allowed_value = (name, value, fallback) => (
    allowed[name]?.includes(String(value || '').toLowerCase())
        ? String(value).toLowerCase()
        : fallback
);

const boolean_value = (value, fallback) => (
    typeof value === 'boolean' ? value : fallback
);

export const normalize_interface_customization_settings = (value = {}) => {
    const source = value && typeof value === 'object' ? value : {};
    const defaults = DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS;
    const zoom = bounded_number(
        source.workspace_zoom_percent, 100, 90, 125,
    );
    const legacy_tab_height = bounded_number(
        source.tab_button_height_px, 15, 15, 30,
    );

    return {
        interface_font_size_px: bounded_number(
            source.interface_font_size_px, 17, 12, 24,
        ),
        interface_density: allowed_value(
            'interface_density', source.interface_density, 'normal',
        ),
        main_sidebar_width_px: bounded_number(
            source.main_sidebar_width_px, 100, 90, 280,
        ),
        contextual_sidebar_width_px: bounded_number(
            source.contextual_sidebar_width_px,
            source.main_sidebar_width_px || 100,
            90,
            280,
        ),
        left_sidebar_fit_content: boolean_value(
            source.left_sidebar_fit_content,
            defaults.left_sidebar_fit_content,
        ),
        top_tab_button_height_px: bounded_number(
            source.top_tab_button_height_px,
            legacy_tab_height,
            15,
            30,
        ),
        left_tab_button_height_px: bounded_number(
            source.left_tab_button_height_px,
            defaults.left_tab_button_height_px,
            15,
            30,
        ),
        lk_tab_button_height_px: bounded_number(
            source.lk_tab_button_height_px,
            defaults.lk_tab_button_height_px,
            20,
            80,
        ),
        tab_gap_px: bounded_number(source.tab_gap_px, 5, 0, 24),
        left_tab_gap_px: bounded_number(
            source.left_tab_gap_px, 5, 0, 24,
        ),
        lk_tab_gap_px: bounded_number(
            source.lk_tab_gap_px, defaults.lk_tab_gap_px, 0, 40,
        ),
        top_tab_title_padding_px: bounded_number(
            source.top_tab_title_padding_px, 8, 0, 24,
        ),
        left_tab_title_padding_px: bounded_number(
            source.left_tab_title_padding_px, 8, 0, 24,
        ),
        input_padding_block_px: bounded_number(
            source.input_padding_block_px, 4, 0, 24,
        ),
        input_padding_inline_px: bounded_number(
            source.input_padding_inline_px, 8, 0, 32,
        ),
        long_tab_names: allowed_value(
            'long_tab_names', source.long_tab_names, 'wrap',
        ),
        remember_last_tab: boolean_value(
            source.remember_last_tab, defaults.remember_last_tab,
        ),
        restore_open_tabs: boolean_value(
            source.restore_open_tabs, defaults.restore_open_tabs,
        ),
        reopen_object_mode: allowed_value(
            'reopen_object_mode', source.reopen_object_mode, 'existing',
        ),
        table_row_height_px: bounded_number(
            source.table_row_height_px, 30, 22, 72,
        ),
        table_font_size_px: bounded_number(
            source.table_font_size_px, 17, 11, 24,
        ),
        sticky_table_header: boolean_value(
            source.sticky_table_header, defaults.sticky_table_header,
        ),
        scrollbars: allowed_value(
            'scrollbars', source.scrollbars, 'normal',
        ),
        animations: allowed_value(
            'animations', source.animations, 'normal',
        ),
        toast_duration_ms: bounded_number(
            source.toast_duration_ms, 10000, 1000, 30000,
        ),
        toast_position: allowed_value(
            'toast_position', source.toast_position, 'top-left',
        ),
        unsaved_close_confirmation: allowed_value(
            'unsaved_close_confirmation',
            source.unsaved_close_confirmation,
            'always',
        ),
        start_section: allowed_value(
            'start_section', source.start_section, 'last',
        ),
        portrait_context_auto_collapse: boolean_value(
            source.portrait_context_auto_collapse,
            defaults.portrait_context_auto_collapse,
        ),
        workspace_zoom_percent: [90, 100, 110, 125].includes(zoom)
            ? zoom
            : 100,
        document_register_view: allowed_value(
            'document_register_view',
            source.document_register_view,
            'none',
        ),
        document_showcase_view: allowed_value(
            'document_showcase_view',
            source.document_showcase_view,
            'tiles',
        ),
        document_showcase_sort: allowed_value(
            'document_showcase_sort',
            source.document_showcase_sort,
            'name_asc',
        ),
    };
};

export const install_interface_customization_settings = (value = {}) => {
    current_settings = normalize_interface_customization_settings(value);

    if (typeof document !== 'undefined') {

        apply_css_values(current_settings);
    }

    if (typeof window !== 'undefined') {
        try {
            const serialized_settings = JSON.stringify(current_settings);
            window.sessionStorage.setItem(
                SETTINGS_CACHE_KEY, serialized_settings
            );

        } catch {
        }

        if (!viewport_listener_installed) {

            window.addEventListener('resize', () => {

                apply_css_values(current_settings);
            });
            viewport_listener_installed = true;
        }

        window.dispatchEvent?.(new CustomEvent(
            'potale:interface-customization-updated',
            { detail: current_settings },
        ));
    }

    return { ...current_settings };
};

export const get_interface_customization_settings = () => ({
    ...current_settings,
});

export const load_interface_customization_settings = (api_service) => {
    if (settings_load_promise) {
        return settings_load_promise;
    }

    settings_load_promise = api_service.df({
        table_name: 'interface_customization_settings',
    }).then((response) => install_interface_customization_settings(
        response?.settings || {},
    )).finally(() => {
        settings_load_promise = null;
    });

    return settings_load_promise;
};

export const start_section_path = (settings = current_settings) => ({
    bux: '/bux',
    lk: '/LK',
    developer: '/developer',
    office: '/office',
}[settings.start_section] || null);

if (typeof window !== 'undefined') {
    try {
        const cached_value = window.sessionStorage.getItem(SETTINGS_CACHE_KEY);
        const cached_settings = JSON.parse(cached_value || 'null');
        if (cached_settings && typeof cached_settings === 'object') {
            install_interface_customization_settings(cached_settings);
        }

    } catch {
    }
}
